import type { EventType } from "../actions";
import type { LocalizedText } from "../meta";
import type { ActionCategory } from "./action";


export const CATEGORY_ORDER: readonly ActionCategory[] = [
    "event",
    "combat",
    "economy",
    "narrative",
    "exploration",
    "interface",
] as const;

export const CATEGORY_LABELS: Record<ActionCategory, LocalizedText> = {
    event: { en: "Triggers", fr: "Déclencheurs" },
    combat: { en: "Combat & Progression", fr: "Combat & Progression" },
    economy: { en: "Economy & Loot", fr: "Économie & Butin" },
    narrative: { en: "Story & Dialogue", fr: "Narration & Dialogue" },
    exploration: { en: "World & Ambiance", fr: "Monde & Ambiance" },
    interface: { en: "Interface", fr: "Interface" },
};

export const CATEGORY_DESCRIPTIONS: Record<ActionCategory, LocalizedText> = {
    event: {
        en: "Entry points that start an action chain when something happens in the world.",
        fr: "Points d'entrée qui démarrent une chaîne d'actions quand quelque chose se passe dans le monde.",
    },
    combat: {
        en: "Define behaviors, manage stats, equip gear, spawn entities and orchestrate defeat sequences.",
        fr: "Définis les comportements, gère les stats, équipe du matériel, fais apparaître des entités et orchestre les séquences de défaite.",
    },
    economy: {
        en: "Give items, roll loot tables, handle currencies, open crafting stations and set timers.",
        fr: "Donne des items, lance des tables de butin, gère les devises, ouvre des ateliers de craft et lance des minuteurs.",
    },
    narrative: {
        en: "Write dialogues, create interactions, send notifications, guide players and add conditional logic.",
        fr: "Écris des dialogues, crée des interactions, envoie des notifications, guide les joueurs et ajoute de la logique conditionnelle.",
    },
    exploration: {
        en: "Teleport players, set the mood with ambiance presets and control HUD stat displays.",
        fr: "Téléporte les joueurs, définis l'ambiance avec des préréglages et contrôle l'affichage des stats à l'écran.",
    },
    interface: {
        en: "Mutate UI nodes — change text, image, color, visibility — from a graph triggered by an OnClick / OnHover / etc.",
        fr: "Modifie des éléments d'interface — texte, image, couleur, visibilité — depuis un graphe déclenché par OnClick / OnHover / etc.",
    },
};

export type EventMeta = {
    type: EventType;
    icon: string;
    label: LocalizedText;
    description: LocalizedText;
};

export const EVENT_META: readonly EventMeta[] = [
    {
        type: "OnSpawn",
        icon: "/icons/event-spawn.svg",
        label: { en: "On spawn", fr: "À l'apparition" },
        description: {
            en: "Fires the moment the entity appears in the world.",
            fr: "Se déclenche au moment où l'entité apparaît dans le monde.",
        },
    },
    {
        type: "OnTouch",
        icon: "/icons/event-touch.svg",
        label: { en: "On touch", fr: "Au contact" },
        description: {
            en: "Fires when a player physically touches the entity.",
            fr: "Se déclenche quand un joueur touche physiquement l'entité.",
        },
    },
    {
        type: "OnConsume",
        icon: "/icons/event-consume.svg",
        label: { en: "On consume", fr: "À la consommation" },
        description: {
            en: "Fires when the player uses the item from the inventory.",
            fr: "Se déclenche quand le joueur utilise l'item depuis l'inventaire.",
        },
    },
    {
        type: "OnDamage",
        icon: "/icons/event-damage.svg",
        label: { en: "On damage", fr: "Quand il subit des dégâts" },
        description: {
            en: "Fires each time the entity takes damage.",
            fr: "Se déclenche à chaque fois que l'entité subit des dégâts.",
        },
    },
    {
        type: "OnDeath",
        icon: "/icons/event-death.svg",
        label: { en: "On death", fr: "À la mort" },
        description: {
            en: "Fires when the entity's vitality reaches zero.",
            fr: "Se déclenche quand la vitalité de l'entité atteint zéro.",
        },
    },
    {
        type: "OnCreate",
        icon: "/icons/event-create.svg",
        label: { en: "On create", fr: "À la création" },
        description: {
            en: "Fires once when the player character is first created.",
            fr: "Se déclenche une seule fois quand le personnage du joueur est créé.",
        },
    },
    {
        type: "OnRespawn",
        icon: "/icons/event-respawn.svg",
        label: { en: "On respawn", fr: "À la réapparition" },
        description: {
            en: "Fires each time the entity respawns after death.",
            fr: "Se déclenche à chaque réapparition de l'entité après la mort.",
        },
    },
    {
        type: "OnEnter",
        icon: "/icons/event-enter.svg",
        label: { en: "On enter", fr: "A l'entree" },
        description: {
            en: "Fires when an item instance enters this Zone item.",
            fr: "Se declenche quand une instance d'item entre dans cet item Zone.",
        },
    },
    {
        type: "OnExit",
        icon: "/icons/event-exit.svg",
        label: { en: "On exit", fr: "A la sortie" },
        description: {
            en: "Fires when an item instance leaves this Zone item.",
            fr: "Se declenche quand une instance d'item quitte cet item Zone.",
        },
    },
    {
        type: "OnZoneEnter",
        icon: "/icons/event-zone-enter.svg",
        label: { en: "On zone enter", fr: "Entre en zone" },
        description: {
            en: "Fires on the occupying item when it enters a Zone item.",
            fr: "Se declenche sur l'item occupant quand il entre dans un item Zone.",
        },
    },
    {
        type: "OnZoneExit",
        icon: "/icons/event-zone-exit.svg",
        label: { en: "On zone exit", fr: "Sort de zone" },
        description: {
            en: "Fires on the occupying item when it leaves a Zone item.",
            fr: "Se declenche sur l'item occupant quand il quitte un item Zone.",
        },
    },
    {
        type: "OnClick",
        icon: "/icons/event-click.svg",
        label: { en: "On click", fr: "Au clic" },
        description: {
            en: "Fires when the player clicks this UI element.",
            fr: "Se déclenche quand le joueur clique sur cet élément d'interface.",
        },
    },
    {
        type: "OnHover",
        icon: "/icons/event-hover.svg",
        label: { en: "On hover", fr: "Au survol" },
        description: {
            en: "Fires when the player's cursor enters this UI element.",
            fr: "Se déclenche quand le curseur du joueur entre sur cet élément.",
        },
    },
    {
        type: "OnUnhover",
        icon: "/icons/event-unhover.svg",
        label: { en: "On unhover", fr: "Fin de survol" },
        description: {
            en: "Fires when the player's cursor leaves this UI element.",
            fr: "Se déclenche quand le curseur du joueur quitte cet élément.",
        },
    },
    {
        type: "OnMouseDown",
        icon: "/icons/event-mousedown.svg",
        label: { en: "On mouse down", fr: "Au clic enfoncé" },
        description: {
            en: "Fires when the player presses the mouse button on this element.",
            fr: "Se déclenche quand le joueur enfonce le bouton de la souris sur cet élément.",
        },
    },
    {
        type: "OnMouseUp",
        icon: "/icons/event-mouseup.svg",
        label: { en: "On mouse up", fr: "Au clic relâché" },
        description: {
            en: "Fires when the player releases the mouse button on this element.",
            fr: "Se déclenche quand le joueur relâche le bouton de la souris sur cet élément.",
        },
    },
    {
        type: "OnVisible",
        icon: "/icons/event-visible.svg",
        label: { en: "On visible", fr: "À l'affichage" },
        description: {
            en: "Fires when this UI element becomes visible on screen.",
            fr: "Se déclenche quand cet élément d'interface devient visible à l'écran.",
        },
    },
    {
        type: "OnHidden",
        icon: "/icons/event-hidden.svg",
        label: { en: "On hidden", fr: "Au masquage" },
        description: {
            en: "Fires when this UI element becomes hidden.",
            fr: "Se déclenche quand cet élément d'interface est masqué.",
        },
    },
    {
        type: "OnMatchReady",
        icon: "/icons/event-spawn.svg",
        label: { en: "On match ready", fr: "Match prêt" },
        description: {
            en: "Fires when a Matchmaker island has been cloned and the match starts.",
            fr: "Se déclenche quand une île Matchmaker a été clonée et que le match démarre.",
        },
    },
    {
        type: "OnMatchFailed",
        icon: "/icons/event-hidden.svg",
        label: { en: "On match failed", fr: "Match échoué" },
        description: {
            en: "Fires when a Matchmaker island could not start.",
            fr: "Se déclenche quand une île Matchmaker ne peut pas démarrer.",
        },
    },
    {
        type: "OnTeamWin",
        icon: "/icons/event-spawn.svg",
        label: { en: "On team win", fr: "Victoire d'équipe" },
        description: {
            en: "Fires for the winning team's members when the match's victory rule is met. Title/Teleport here target that team.",
            fr: "Se déclenche pour les membres de l'équipe gagnante quand la règle de victoire du match est atteinte. Title/Teleport ici ciblent cette équipe.",
        },
    },
    {
        type: "OnTeamDefeated",
        icon: "/icons/event-hidden.svg",
        label: { en: "On team defeated", fr: "Équipe éliminée" },
        description: {
            en: "Fires for an eliminated team's members when its defeat rule is met. Title/Teleport here target that team.",
            fr: "Se déclenche pour les membres d'une équipe éliminée quand sa règle de défaite est atteinte. Title/Teleport ici ciblent cette équipe.",
        },
    },
    {
        type: "OnMatchQuit",
        icon: "/icons/event-exit.svg",
        label: { en: "On match quit", fr: "Sortie de match" },
        description: {
            en: "Fires for a player when they actually leave a Matchmaker session, after teleporting out, respawning, or disconnecting.",
            fr: "Se déclenche pour un joueur quand il quitte réellement une session Matchmaker, après téléportation, respawn ou déconnexion.",
        },
    },
];


export const EVENT_META_BY_TYPE = new Map<EventType, EventMeta>(
    EVENT_META.map((m) => [m.type, m]),
);


export const findEventMeta = (type: string): EventMeta | undefined =>
    EVENT_META_BY_TYPE.get(type as EventType);


export const getEventMeta = (type: EventType): EventMeta => {
    const found = findEventMeta(type);
    if (!found) throw new Error(`Missing EVENT_META entry for '${type}'`);
    return found;
};
